"use client"

import { useState, useEffect } from "react"
import { HelpCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { useMobile } from "@/hooks/use-mobile"
import ThemeToggle from "@/components/theme-toggle"
import { LanguageSwitcher } from "@/components/LanguageSwitcher"
import { useTranslation } from "react-i18next"

interface GesturesHelpProps {
  className?: string
}

export default function GesturesHelp({ className }: GesturesHelpProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isTouchDevice, setIsTouchDevice] = useState(false)
  const isMobile = useMobile()
  const { t } = useTranslation("common");

  useEffect(() => {
    // Check if device supports touch
    const checkTouchDevice = () => {
      setIsTouchDevice(
        "ontouchstart" in window ||
        navigator.maxTouchPoints > 0 ||
        (navigator as any).msMaxTouchPoints > 0 ||
        window.matchMedia("(pointer: coarse)").matches,
      )
    }

    checkTouchDevice()

    // Add event listener for orientation changes which might affect device detection
    window.addEventListener("orientationchange", checkTouchDevice)

    return () => {
      window.removeEventListener("orientationchange", checkTouchDevice)
    }
  }, [])

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className={`p-0 w-4 h-4 ${className}`} onClick={() => setIsOpen(true)}>
              <HelpCircle className="h-5 w-5" />
              <span className="sr-only">{t("common:gestureHelp.helpIcon")}</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t("common:gestureHelp.viewHelp")}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader className="flex justify-between items-start">
            <div className="text-start rtl:text-right">
              <DialogTitle>{t("common:gestureHelp.title")}</DialogTitle>
              <DialogDescription>{t("common:gestureHelp.description")}</DialogDescription>
            </div>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            {/* Show mouse controls only if not a touch-only device or if it's a hybrid device */}
            {(!isTouchDevice || !isMobile) && (
              <div className="grid grid-cols-[24px_1fr] rtl:grid-cols-[1fr_24px] items-start gap-4">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs rtl:order-2">
                  🖱️
                </span>
                <div className="space-y-1 rtl:order-1 rtl:text-right">
                  <p className="text-sm font-medium leading-none">{t("common:gestureHelp.mouse.title")}</p>
                  <p className="text-sm text-muted-foreground">
                    <strong>
                      {t("common:gestureHelp.mouse.rotate.action")}
                    </strong>{" "}
                    {t("common:gestureHelp.mouse.rotate.description")}
                    <br />
                    <strong>
                      {t("common:gestureHelp.mouse.zoom.action")}
                    </strong>{" "}
                    {t("common:gestureHelp.mouse.zoom.description")}
                    <br />
                    <strong>
                      {t("common:gestureHelp.mouse.pan.action")}
                    </strong>{" "}
                    {t("common:gestureHelp.mouse.pan.description")}
                  </p>
                </div>
              </div>
            )}

            {/* Show touch controls only if it's a touch device */}
            {isTouchDevice && (
              <div className="grid grid-cols-[24px_1fr] rtl:grid-cols-[1fr_24px] items-start gap-4">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs rtl:order-2">
                  👆
                </span>
                <div className="space-y-1 rtl:order-1 rtl:text-right">
                  <p className="text-sm font-medium leading-none">{t("common:gestureHelp.touch.title")}</p>
                  <p className="text-sm text-muted-foreground">
                    <strong>
                      {t("common:gestureHelp.touch.rotate.action")}
                    </strong>{" "}
                    {t("common:gestureHelp.touch.rotate.description")}
                    <br />
                    <strong>
                      {t("common:gestureHelp.touch.zoom.action")}
                    </strong>{" "}
                    {t("common:gestureHelp.touch.zoom.description")}
                    <br />
                    <strong>
                      {t("common:gestureHelp.touch.pan.action")}
                    </strong>{" "}
                    {t("common:gestureHelp.touch.pan.description")}
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-center items-center gap-3">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
